const STORAGE_KEY = "bookingManagement_listFilters_v1";

export type BookingSearchFields = {
  bookingId: string;
  userName: string;
  mobileNo: string;
  plan: string;
  vehicle: string;
  vehicleNumber: string;
  registrationNumber: string;
};

export const emptyBookingSearchFields = (): BookingSearchFields => ({
  bookingId: "",
  userName: "",
  mobileNo: "",
  plan: "",
  vehicle: "",
  vehicleNumber: "",
  registrationNumber: "",
});

export type PersistedBookingListFilters = {
  status: string;
  searchFields: BookingSearchFields;
  dateRange: [string | null, string | null];
  page: number;
  rowsPerPage: number;
};

export function loadBookingListFilters(): PersistedBookingListFilters | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedBookingListFilters> & {
      searchTerm?: string;
    };
    if (typeof parsed !== "object" || parsed === null) return null;

    const searchFieldsRaw = parsed.searchFields;
    const legacySearch =
      typeof parsed.searchTerm === "string" ? parsed.searchTerm : "";

    const searchFields: BookingSearchFields =
      searchFieldsRaw &&
      typeof searchFieldsRaw === "object" &&
      !Array.isArray(searchFieldsRaw)
        ? {
            bookingId:
              typeof searchFieldsRaw.bookingId === "string"
                ? searchFieldsRaw.bookingId
                : "",
            userName:
              typeof searchFieldsRaw.userName === "string"
                ? searchFieldsRaw.userName
                : legacySearch,
            mobileNo:
              typeof searchFieldsRaw.mobileNo === "string"
                ? searchFieldsRaw.mobileNo
                : "",
            plan:
              typeof searchFieldsRaw.plan === "string"
                ? searchFieldsRaw.plan
                : "",
            vehicle:
              typeof searchFieldsRaw.vehicle === "string"
                ? searchFieldsRaw.vehicle
                : "",
            vehicleNumber:
              typeof searchFieldsRaw.vehicleNumber === "string"
                ? searchFieldsRaw.vehicleNumber
                : "",
            registrationNumber:
              typeof searchFieldsRaw.registrationNumber === "string"
                ? searchFieldsRaw.registrationNumber
                : "",
          }
        : {
            ...emptyBookingSearchFields(),
            userName: legacySearch,
          };

    const rawRange = parsed.dateRange;
    const dateRange: [string | null, string | null] =
      Array.isArray(rawRange) &&
      rawRange.length === 2 &&
      (rawRange[0] === null || typeof rawRange[0] === "string") &&
      (rawRange[1] === null || typeof rawRange[1] === "string")
        ? [rawRange[0], rawRange[1]]
        : [null, null];

    return {
      status: typeof parsed.status === "string" ? parsed.status : "",
      searchFields,
      dateRange,
      page:
        typeof parsed.page === "number" && Number.isFinite(parsed.page)
          ? Math.max(0, Math.floor(parsed.page))
          : 0,
      rowsPerPage:
        typeof parsed.rowsPerPage === "number" &&
        Number.isFinite(parsed.rowsPerPage)
          ? Math.max(1, Math.floor(parsed.rowsPerPage))
          : 10,
    };
  } catch {
    return null;
  }
}

export function saveBookingListFilters(state: PersistedBookingListFilters): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota / private mode
  }
}

function parsePositiveInt(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const n = Number.parseInt(trimmed.replace(/\D/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function buildBookingSearchApiParams(filters: BookingSearchFields): {
  bookingId: number;
  userName: string;
  mobileNumber: string;
  planId: number;
  vehicleName: string;
  vehicleNumber: string;
  registrationNumber: string;
} {
  return {
    bookingId: parsePositiveInt(filters.bookingId),
    userName: filters.userName.trim(),
    mobileNumber: filters.mobileNo.trim(),
    planId: parsePositiveInt(filters.plan),
    vehicleName: filters.vehicle.trim(),
    vehicleNumber: filters.vehicleNumber.trim(),
    registrationNumber: filters.registrationNumber.trim(),
  };
}
